// Publish an APPROVED version onto the creative's distributions in one server
// transaction (POST /versions/:id/publish). When the creative has no
// distribution yet, the Distribution step comes first — where the Tag will be
// used (platform catalog), the format and honest compatibility (7.6F.2) —
// and the version is published onto it; that is where its permanent Tag
// comes from. The creative itself never carried a platform.
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";
import { api, call } from "@/api/client";
import { ApiProblem } from "@/api/errors";
import { keys } from "@/api/hooks";
import type { CreativeProduct, Distribution } from "@/api/types";
import { displayFormat } from "@/lib/labels";
import { Button } from "@/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/ui/dialog";
import { Callout } from "@/ui/layout";
import { ServingStateBadge } from "@/components/status";
import { DistributionForm, useVersionFormats } from "../distributions/distribution-form";
import { CompatibilityBadge } from "../platforms/compatibility-summary";

export function PublishDialog({
  orgId,
  creativeId,
  product,
  versionId,
  versionNumber,
  distributions,
  canCreateDistribution,
  open,
  onOpenChange,
}: {
  orgId: string;
  creativeId: string;
  product: CreativeProduct | undefined;
  versionId: string;
  versionNumber: number;
  distributions: Distribution[];
  canCreateDistribution: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const active = distributions.filter((d) => !d.archived);
  const [selected, setSelected] = useState<string[]>(active.map((d) => d.id));
  const [error, setError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);
  const needsDistribution = active.length === 0;
  const formats = useVersionFormats(orgId, open && needsDistribution ? versionId : undefined);

  const publishOnto = async (targets: string[]) => {
    const result = await call(() =>
      api.POST("/orgs/{orgId}/versions/{versionId}/publish", {
        params: { path: { orgId, versionId } },
        body: { distributionIds: targets },
      })
    );
    await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
    onOpenChange(false);
    const first = result.distributions[0];
    if (first) await navigate({ to: `/o/${orgId}/distributions/${first.id}`, search: { published: 1 } });
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setPending(true);
    setError(undefined);
    try {
      await publishOnto(selected);
    } catch (problem) {
      setError(problem instanceof ApiProblem ? problem.message : "No se pudo publicar.");
    } finally {
      setPending(false);
    }
  };

  const cancel = (
    <Button
      variant="secondary"
      onClick={() => {
        onOpenChange(false);
      }}
    >
      Cancelar
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={`Publicar versión ${String(versionNumber)}`}
        description={
          needsDistribution
            ? "Primero elige dónde vas a utilizar el Tag. Después publicamos la versión en esa distribución; su Tag permanente no cambia con futuras publicaciones."
            : "La versión publicada pasará a entregarse en las distribuciones que elijas. Su Tag permanente no cambia."
        }
        className={needsDistribution ? "max-h-[85vh] max-w-xl overflow-y-auto" : undefined}
      >
        {needsDistribution ? (
          !canCreateDistribution ? (
            <>
              <Callout tone="warning">
                Este creativo aún no tiene distribuciones. Pide a un responsable que cree una.
              </Callout>
              <DialogFooter>{cancel}</DialogFooter>
            </>
          ) : product && (formats.isSuccess || formats.isError) ? (
            <DistributionForm
              key={formats.data?.join(",") ?? "none"}
              orgId={orgId}
              creativeId={creativeId}
              product={product}
              formats={formats.data ?? []}
              canAddPlatform
              submitLabel="Crear distribución y publicar"
              pendingLabel="Publicando…"
              onCreated={async (created) => {
                await publishOnto([created.id]);
              }}
              footer={(submitButton) => (
                <DialogFooter>
                  {cancel}
                  {submitButton}
                </DialogFooter>
              )}
            />
          ) : (
            <p className="text-sm text-muted">Cargando…</p>
          )
        ) : (
          <form className="grid gap-4" onSubmit={(e) => void submit(e)}>
            <fieldset className="grid gap-2">
              <legend className="mb-1 text-sm font-medium">Distribuciones</legend>
              {active.map((d) => (
                <label
                  key={d.id}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-line px-3 py-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(d.id)}
                    onChange={(e) => {
                      setSelected((prev) => (e.target.checked ? [...prev, d.id] : prev.filter((x) => x !== d.id)));
                    }}
                  />
                  <span className="min-w-0 flex-1">
                    {d.name} · {d.platform.name} {displayFormat(d.format)}
                  </span>
                  <CompatibilityBadge status={d.compatibility.overall} />
                  <ServingStateBadge state={d.serving.effectiveState} />
                </label>
              ))}
              <p className="text-xs text-muted">Si no eliges ninguna, la versión queda publicada sin activarse.</p>
            </fieldset>
            {error ? (
              <p className="text-sm text-danger" role="alert">
                {error}
              </p>
            ) : null}
            <DialogFooter>
              {cancel}
              <Button type="submit" variant="primary" disabled={pending}>
                Publicar
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
